import { isValidElement, type ReactNode } from "react";

import { addressDisplay, isAddressLike } from "@/shared/lib/formatting";

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function fileDisplayName(value: Record<string, unknown>) {
  if (typeof value.display === "string" && value.display) return value.display;
  const nested = value.value as Record<string, unknown> | undefined;
  return (
    value.file_name ||
    value.fileName ||
    value.uploaded_file_name ||
    value.name ||
    value.original_filename ||
    nested?.file_name ||
    (typeof value.uuid === "string" ? value.uuid.slice(0, 8) : null) ||
    (typeof value.uploaded_file_uuid === "string"
      ? value.uploaded_file_uuid.slice(0, 8)
      : null)
  );
}

function fileDisplayUrl(value: Record<string, unknown>) {
  const nested = value.value as Record<string, unknown> | undefined;
  return value.file_url || value.fileUrl || value.url || nested?.file_url;
}

function isFileObject(value: Record<string, unknown>) {
  if (value.display && typeof value.value === "object" && value.value !== null) {
    const nested = value.value as Record<string, unknown>;
    if (nested.uuid || nested.file_name || nested.file_url) return true;
  }
  return Boolean(
    value.file_url ||
      value.fileUrl ||
      value.file_name ||
      value.fileName ||
      value.uploaded_file_name ||
      value.uploaded_file_uuid,
  );
}

// Money fields are stored as a { amount, currency } object. When no
// amount has been entered yet (e.g. an estimate before a decision) the
// value is just { currency: "AUD" }. Recognise this shape precisely so
// it formats as currency (or a dash) instead of leaking the raw dict.
function isMoneyObject(value: Record<string, unknown>) {
  const keys = Object.keys(value);
  return keys.length > 0 && typeof value.currency === "string" && keys.every((k) => k === "amount" || k === "currency");
}

// An ENTITY_SEARCH value (a picked resource reference, e.g. an installed
// component product or a dealer/retailer partnership) is stored as a composite
// object like { uuid, name } (or a dunder display column such as
// { partnership__name }). Recognise it so the card surfaces the human-readable
// name instead of dumping the raw dict as JSON. Mirrors fmtEntityReference in
// shared/lib/forms/display.tsx.
function entityReferenceLabel(value: Record<string, unknown>): string | null {
  const label = [value.display, value.name, value.label].find(
    (v): v is string => typeof v === "string" && v.trim().length > 0,
  );
  if (label) return label;
  const dunder = Object.entries(value).find(
    ([k, v]) => k.includes("__") && typeof v === "string" && v.trim().length > 0,
  );
  return dunder ? (dunder[1] as string) : null;
}

function renderMoneyObject(value: Record<string, unknown>): ReactNode {
  const amount = value.amount;
  if (amount === null || amount === undefined || amount === "") return "-";
  const num = typeof amount === "number" ? amount : Number(amount);
  if (Number.isNaN(num)) return "-";
  const hasDecimals = num % 1 !== 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: value.currency as string,
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(num);
  } catch {
    return `${value.currency as string} ${num}`;
  }
}

function renderFileObject(value: Record<string, unknown>, key?: string): ReactNode {
  const label = fileDisplayName(value) || "Uploaded file";
  const url = fileDisplayUrl(value);

  if (typeof url === "string" && url) {
    return (
      <a
        key={key}
        href={url}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => event.stopPropagation()}
        className="text-[var(--green)] underline-offset-2 hover:underline"
      >
        {String(label)}
      </a>
    );
  }

  return String(label);
}

export function normaliseDetailCardDisplayValue(value: unknown): ReactNode {
  if (value === null || value === undefined || typeof value !== "object" || isValidElement(value)) {
    return value as ReactNode;
  }

  if (Array.isArray(value)) {
    const displayValues = value
      .map((item, index) => {
        if (isValidElement(item)) return item;
        if (isRecord(item) && isFileObject(item)) {
          return renderFileObject(item, `file-${index}`);
        }
        if (isRecord(item) && isAddressLike(item)) {
          return addressDisplay(item);
        }
        if (isRecord(item)) {
          return entityReferenceLabel(item) ?? JSON.stringify(item);
        }
        return item === null || item === undefined || item === "" ? null : String(item);
      })
      .filter((item): item is ReactNode => item !== null && item !== undefined && item !== "");

    if (displayValues.length === 0) return "-";
    if (displayValues.every((item) => typeof item === "string")) {
      return displayValues.join(", ");
    }
    return displayValues.map((item, index) => (
      <span key={index} className="ml-2 first:ml-0">
        {item}
      </span>
    ));
  }

  if (isFileObject(value)) {
    return renderFileObject(value);
  }

  if (isAddressLike(value)) {
    return addressDisplay(value) || "-";
  }

  if (isMoneyObject(value)) {
    return renderMoneyObject(value);
  }

  const reference = entityReferenceLabel(value);
  if (reference) return reference;

  return JSON.stringify(value);
}
